import { useCallback, useEffect, useRef, useState } from "react";
import {
  trimPathRight,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import WebshareApi from "../api";
import {
  useCommittedLocation,
  useRouterHasSettled,
} from "@/routing/committedLocation";
import { deleteToken, getToken } from "./authentication";
import React from "react";
import useSWR from "@/hooks/useSWR";
import { SubscriptionType } from "../types/SubscriptionTypes";
import { AuthContext } from "./RouteGuards";
import { SessionMutateContext } from "../context/SessionMutateContext";
import { useFederatedUser } from "./federation";
import useSubuser from "@/hooks/useSubuser";
import useProfile from "@/hooks/useProfile";
import LoadingView from "@/components/common/LoadingView";
import useAccountActivation from "@/hooks/useAccountActivation";
import timezoneUtils from "@/utils/TimezoneUtils";
import { identifyKlaviyoProfile } from "@/tracking/klaviyo";
import useSignedInUser from "@/tracking/useSignedInUser";
import { patchProxyConfig } from "@/api/proxy";
import { AxiosError } from "axios";
import usePlan from "@/hooks/usePlan";
import { ProxyConfigurationIPAuthorizationCountryCodesType } from "../types";
import usePlans from "@/hooks/usePlans";
import OnboardingChecklistFAB from "@/ui/OnboardingChecklistFAB";
import IntercomProviderInternal from "@/providers/IntercomProviderInternal";
import BugReportButton from "@/components/BugReport/BugReportButton";
import { safeNextUrl } from "@/utils/safeNextUrl";
import {
  ACCOUNT_DELETED_PATHNAME,
  isAccountDeletedError,
} from "./accountDeleted";
import shouldHoldPublicRender from "./shouldHoldPublicRender";

/**
 * Route *patterns*, in the router's own `to` spelling, `/packages/$packageId`,
 * the very string `navigate({ to })` takes. Two things depend on that spelling
 * and neither is negotiable:
 *
 * - Every entry has to name a real route, or the route it names silently stops
 *   being public and logged-out users get bounced to login. The frozen oracle
 *   asserts it (`tanstackAppRouter.spec.ts`, "every RouteGuard pattern
 *   corresponds to a real, producible route"), reading these arrays out of this
 *   file by text and checking them against the generated route tree.
 * - The arrays are read through two different lenses below, against the
 *   CONCRETE url path, which no dynamic route ever matches, and against the
 *   RESOLVED route pattern, which it always does. A concrete public URL can be
 *   redirected by one gate and rendered by the other; that asymmetry is
 *   current behaviour (ANO-084) and is pinned by
 *   `__tests__/RouteGuardDynamicRoutes.test.tsx`.
 *
 * `/$` is the catch-all route, which renders the 404 page: every URL matching
 * no other route resolves to it, so it is public exactly as `/404` is.
 * `/logout` and `/_error` are the two entries that name no route at all
 * (ANO-002), nothing resolves to them, and they are here only so the concrete
 * lens leaves those URLs alone.
 */
const loggedoutPaths = ["/login", "/register", "/packages/$packageId"];

// Named because the redirect gate below compares against it. It has to be
// declared between the two arrays: the oracle checks route patterns by slicing
// this file's text around them, so a copy declared anywhere else is the one
// pattern in this file nothing verifies still names a real route.
const NOT_FOUND_ROUTE_PATTERN = "/$";

const publicPaths = [
  ...loggedoutPaths,
  "/calculator",
  "/activation/$activationtoken/confirm",
  "/activation/confirm",
  "/_error",
  "/403",
  "/404",
  NOT_FOUND_ROUTE_PATTERN,
  "/logout",
  "/extension-captcha-challenge",
  "/accountsuspension",
  "/profile/deleteaccount/deleted",
  "/express-checkout",
  "/auth/google",
  "/accountdeleted",
  /** Routes below should be reachable even if the user is logged in */
  "/resetpassword/$passwordresettoken/confirm",
  "/resetpassword/newpassword",
  "/login/forgotpassword",
];

type GuardProps = React.PropsWithChildren<{}>;

export default function RouteGuard({ children }: GuardProps) {
  const { deleteFederationToken } = useFederatedUser();
  const { mutateSubuser } = useSubuser();

  const { mutateProfile, profile, profileError, isProfileLoading } =
    useProfile();
  const navigate = useNavigate();
  // The settled location, not the pending one: `next=` must capture the URL the
  // user is being bounced away from, never the one they are heading to.
  const location = useCommittedLocation();
  /**
   * The pattern of the route the URL resolved to, in the same `to` spelling as
   * the arrays above: the deepest match's `fullPath`, normalised by the
   * router's own `trimPathRight`, the exact expression TanStack itself uses to
   * derive a route's `to`, so an index route's `/login/` reads as `/login`.
   * Derived inside the selector so the component re-renders on a pattern
   * change rather than on every router state transition.
   */
  const routePattern = useRouterState({
    select: (state) =>
      trimPathRight(state.matches[state.matches.length - 1]?.fullPath ?? "/"),
  });
  /**
   * A latch, not a loading flag (ANO-079): held work is released once, and the
   * URL captured at that moment is the one the redirect returns to.
   */
  const isRouterReady = useRouterHasSettled();

  //Called by login, register and logout functions.
  const [sessionToken, setSessionToken] = useState<string | undefined>(
    getToken()
  );
  const { activationStatus, mutateActivationStatus } =
    useAccountActivation(sessionToken);
  const [isTimeZoneSet, setIsTimeZoneSet] = useState(false);
  /**
   * The account-deleted redirect already in flight, if any, one at a time,
   * because that is what lets its promise settle at all.
   *
   * `navigate` hands back the router's `commitLocationPromise`, and every
   * further `navigate` call *replaces* it: a superseded promise is only
   * resolved once the newest one is. `authCheck` re-runs on every router state
   * change, and each of those changes is caused by the navigation it just
   * issued, so without this latch the branch below re-navigated on every
   * render and none of the promises it handed out could ever settle. Measured
   * against the production build with every private endpoint returning 403
   * `account_deleted`: 11,150 `navigate` calls in 20s, zero resolutions, zero
   * rejections, ~220 React "maximum update depth exceeded" errors, and the
   * session cookie still live at the end, which is what the oracle's
   * @SCN-016 catches. Next's `router.push` settled per call, so nothing here
   * needed a latch before the port.
   */
  const accountDeletedRedirect = useRef<Promise<unknown> | undefined>(
    undefined
  );

  if (sessionToken) {
    WebshareApi.setAuthToken(sessionToken);
  }

  //Re-rendered on each call to SessionMutateContext.setSetSessinToken()s
  //Uses sesseionToken based conditional fetching with extra token argument.
  //Fetch conditionally https://swr.vercel.app/docs/conditional-fetching#conditional
  //Multiple arguments https://swr.vercel.app/docs/arguments#multiple-arguments
  const {
    data: subscription,
    mutate: mutateSubscription,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
  } = useSWR<SubscriptionType, AxiosError<{ code: string }>>({
    enabled: !!sessionToken,
    url: "/subscription/",
  });

  const {
    data: plan,
    mutate: mutatePlan,
    error: planError,
    isLoading: isPlanLoading,
  } = usePlan(subscription?.plan || "default");

  const { isLoading: isPlansLoading } = usePlans(!!sessionToken);

  const mutateAll = useCallback(async () => {
    //Calling subscription will also cause plan to mutate due to dependant id.
    await mutateSubscription();
    await mutateSubuser();
    await patchProxyConfig<ProxyConfigurationIPAuthorizationCountryCodesType>(
      { ip_authorization_country_codes: null },
      "default"
    );
  }, [mutateSubscription, mutateSubuser]);

  const authCheck = useCallback(() => {
    // `href` is the router's own `pathname + searchStr + hash`, so `?next=`
    // now carries a deep link's anchor through the login round-trip —
    // `safeNextUrl` keeps it and the post-login `navigate` takes an href, so
    // it reaches the destination (R-2036).
    const url = location.href;
    const path = location.pathname;
    const token = getToken();

    if (!isRouterReady) return;

    // Getting SWR errors
    if (token && (profileError || subscriptionError)) {
      if (
        profileError?.response?.status === 403 ||
        subscriptionError?.response?.status === 403 ||
        planError?.response?.status === 403
      ) {
        // Account deleted. The backend returns the code on every endpoint, but
        // the responses land in any order, keying off profileError alone
        // dead-ends when the subscription/plan 403 arrives first (r-1806).
        const accountDeletedError = [
          profileError,
          subscriptionError,
          planError,
        ].find(isAccountDeletedError);
        if (accountDeletedError) {
          if (
            !!WebshareApi.getFederationToken() &&
            !routePattern.includes("403")
          ) {
            void navigate({
              to: "/403",
              search: {
                errorMessage: accountDeletedError.response?.data?.detail,
              },
            });
          } else if (!WebshareApi.getFederationToken()) {
            // Navigate before clearing the session: clearing it re-runs
            // authCheck, whose logged-out branch races this navigation back to
            // /login and strands the user on an empty form (r-1806). The
            // promise must not settle before the navigation commits, which is
            // `navigate`'s contract and is pinned by
            // `__tests__/RouteGuardAccountDeleted.test.tsx`.
            if (!accountDeletedRedirect.current) {
              const redirect = Promise.resolve(
                navigate({ to: ACCOUNT_DELETED_PATHNAME })
              );
              accountDeletedRedirect.current = redirect;
              redirect
                .then(() => {
                  deleteToken();
                  setSessionToken(undefined);
                  deleteFederationToken();
                })
                // A competing navigation cancels this push and rejects it. The
                // session is left intact on purpose: releasing the latch is
                // enough, because the SWR errors persist and the next authCheck
                // run redirects again.
                .catch(() => {
                  accountDeletedRedirect.current = undefined;
                });
            }
            return;
          }
          // Account suspended
        } else if (
          profileError?.response?.data?.code === "account_suspended" &&
          !routePattern.includes("accountsuspension")
        ) {
          void navigate({ to: "/accountsuspension" });
          // 2FA needed
        } else {
          if (
            !routePattern.includes("403") &&
            !routePattern.includes("accountsuspension") &&
            profileError?.response?.data?.code !== "2fa_needed" &&
            subscriptionError?.response?.data?.code !== "2fa_needed" &&
            planError?.response?.data?.code !== "2fa_needed"
          ) {
            const errorMessage = profileError?.response?.data?.detail;
            if (errorMessage) {
              void navigate({
                to: "/403",
                search: { errorMessage },
              });
            }
          }
        }
      } else {
        //If there is a 401 error due to token logout the user.
        if (
          profileError?.response?.status === 401 ||
          subscriptionError?.response?.status === 401 ||
          planError?.response?.status === 401
        ) {
          // Public paths (express-checkout, register, calculator, etc.)
          // render fine without auth, punting the user to /login on a
          // transient 401 destroys their in-progress state (a half-finished
          // express-checkout purchase, mid-fill register form, etc.). Let
          // SWR's default error-retry recover instead; if the token really
          // is dead the user's next authenticated action will surface it.
          if (publicPaths.includes(routePattern)) {
            return;
          }
          deleteToken();
          setSessionToken(undefined);
          deleteFederationToken();
          if (!routePattern.includes("/login")) {
            void navigate({ to: "/login", search: { next: url } });
          }
        }
      }
    }

    //Redirect logged in user visiting /login.
    if (token && loggedoutPaths.includes(path)) {
      // The router already parsed and decoded the query string; hand-splitting
      // `url` on "?" and "=" re-derives it and would swallow a hash on /login
      // into the destination.
      const { next } = location.search as Record<string, string | undefined>;

      // A built href, not `to`: the destination is only known at runtime, and
      // `safeNextUrl` hands back a path that may already carry a query string.
      void navigate({ href: safeNextUrl(next) });
    }

    // No token but is a loggeout path.
    // loggedout paths with dynamic parameters should use the resolved route
    // pattern instead of path, otherwise it will not any value in the
    // loggedPaths array
    if (!token && loggedoutPaths.includes(routePattern)) {
      return;
    }

    // redirect to login page if accessing a private page and not logged in
    //
    // The catch-all is the exception. It renders the 404 page and is already
    // listed as public, but only the pattern lens can see that: no concrete
    // not-found URL is in `publicPaths`, so the concrete gate sent every 404 to
    // /login. That was invisible while this app served only the dashboard and
    // is not once it serves public pages, every marketing URL not yet ported
    // is a 404, and a reader who lands on one must see "not found", not a login
    // form. Narrowed to the catch-all on purpose: the wider concrete/pattern
    // asymmetry is ANO-084 and stays pinned.
    if (
      !token &&
      !publicPaths.includes(path) &&
      routePattern !== NOT_FOUND_ROUTE_PATTERN
    ) {
      setSessionToken(undefined);
      void navigate({ to: "/login", search: { next: url } });
      return;
    }
  }, [
    deleteFederationToken,
    isRouterReady,
    location,
    navigate,
    planError,
    profileError,
    routePattern,
    subscriptionError,
  ]);

  //TODO: Test running authCheck without events.
  useEffect(() => {
    setSessionToken(getToken());
    authCheck();
  }, [authCheck]);

  useEffect(() => {
    if (profile) {
      timezoneUtils.setTimezone(profile.timezone);
      setIsTimeZoneSet(true);
    }
  }, [profile]);

  useSignedInUser(profile);

  useEffect(() => {
    if (profile?.email && activationStatus?.email_is_verified) {
      identifyKlaviyoProfile({
        email: profile.email,
        first_name: profile.first_name ?? undefined,
        last_name: profile.last_name ?? undefined,
      });
    }
  }, [profile, activationStatus]);

  const isAuthDataReady = !!(
    profile &&
    subscription &&
    plan &&
    timezoneUtils.getTimezone() &&
    isTimeZoneSet
  );
  const holdPublicRender = shouldHoldPublicRender({
    sessionToken,
    isAuthDataReady,
    hasAuthError: !!(profileError || subscriptionError || planError),
    pathname: location.pathname,
    routePattern,
    loggedoutPaths,
  });

  return isAuthDataReady ||
    (publicPaths.includes(routePattern) && !holdPublicRender) ? (
    <SessionMutateContext.Provider
      value={{
        mutatePlan,
        mutateProfile,
        mutateSubscription,
        mutateAll,
        setSessionToken,
        mutateActivationStatus,
      }}
    >
      <AuthContext.Provider
        value={{
          isLoggedIn: !!sessionToken && !!(profile && subscription && plan),
          isLoading:
            isSubscriptionLoading ||
            isPlanLoading ||
            isProfileLoading ||
            isPlansLoading,
          profile,
          subscription,
          plan,
          activationStatus,
        }}
      >
        <IntercomProviderInternal>
          {children}
          <OnboardingChecklistFAB />
          <BugReportButton />
        </IntercomProviderInternal>
      </AuthContext.Provider>
    </SessionMutateContext.Provider>
  ) : (
    <SessionMutateContext.Provider
      value={{
        mutatePlan,
        mutateProfile,
        mutateSubscription,
        mutateAll,
        setSessionToken,
        mutateActivationStatus,
      }}
    >
      <LoadingView />
    </SessionMutateContext.Provider>
  );
}
